import {merge} from 'lodash';
import Assessment from '../assessment';
import AssessmentResult from '../../../values/AssessmentResult';
import wordCountInText from '@axyseo/languageProcessing/researches/wordCountInText';
import {MAIN_CONTENT_POINTS, TEXT_LENGTH_ID} from '@axyseo/const/analysis';

/**
 * Represents an assessment that checks the length of the text and gives feedback accordingly.
 */
 class TextLengthAssessment extends Assessment {
  /**
   * Sets the identifier and the config.
   *
   * @param {Object} [config] The configuration to use.
   *
   * @returns {void}
   */
  constructor(config = {}) {
    super();

    const defaultConfig = {
      id: TEXT_LENGTH_ID,
      ctaType: 'fix',
      docUrl:
        'https://docs.avada.io/seo-suite-help-center/seo-on-page-audit/checklist#content-length-off-target',
      priority: 'high',
      fixPosition: 'description',
      title: 'Content length ',
      content: {
        good: 'Content length on target',
        bad:
          'Write 300 to 500 words. Cover features, specs, sizing, use, and care. '
      }
    };

    this.identifier = TEXT_LENGTH_ID;
    this._config = merge(defaultConfig, config);
  }

  /**
   * Executes the Assessment and returns a result.
   *
   * @param {Paper}       paper       The Paper object to assess.
   * @param {Researcher}  researcher  The Researcher object containing all available researches.
   *
   * @returns {AssessmentResult} The result of the assessment, containing both a score and a descriptive text.
   */
  getResult({paper, researcher}) {
    const calculatedResult = this.calculateResult(paper);
    const assessmentResult = new AssessmentResult({config: this._config});
    assessmentResult.setScore(calculatedResult.score);
    assessmentResult.setStatus(calculatedResult.status);

    return assessmentResult;
  }

  /**
   *
   * @param paper
   * @returns {{score: number, status: string}}
   */
  calculateResult(paper) {
    const pageType = paper.getPageType();
    const { count: wordCount } = wordCountInText(paper);
    const rules = {
      product: {
        min: 300,
        max: 500,
        badMessage:
          'Write 300 to 500 words. Cover features, specs, sizing, use, and care.',
      },
      collection: {
        min: 150,
        max: 300,
        badMessage:
          'Write 150 to 300 words. Present the range and give quick buying guidance.'
      },
      article: {
        min: 600,
        max: 1000,
        badMessage:
          'Write 600 to 1000 words. Explain the topic with clear sections and examples.'
      },
      page: {
        min: 600,
        max: 1000,
        badMessage:
          'Write 600 to 1000 words. Provide clear, well-structured info for the page’s purpose.'
      }
    };

    let status = 'good';
    const rule = rules[pageType];

    if (rule && (wordCount < rule.min || wordCount > rule.max)) {
      status = 'bad';
      this._config = merge(this._config, {
        content: {
          good: 'Content length on target',
          bad: rule.badMessage
        },
        type: pageType
      });
    }

    return {
      score: this.getScore(MAIN_CONTENT_POINTS, status) || 0,
      status
    };
  }

  /**
   * Checks whether the paper has a keyword and a slug.
   *
   * @param {Paper}       paper       The paper to use for the assessment.
   * @param {Researcher}  researcher  The researcher object.
   *
   * @returns {boolean} True if the paper contains a keyword and a slug, and if the keywordCountInSlug research is available on the researcher.
   */
  isApplicable(paper, researcher) {
    return true;
  }
}

export default TextLengthAssessment;
